/**
 * PublicLeagueView — read-only league page reached via a sharing token
 * (#/leagues/<sharingToken>), with NO login required.
 *
 * This is the public face of the GDPR design: every name shown here is already
 * obfuscated server-side ("Carlos T."), and there are no edit/report controls.
 * The router injects <meta name="robots" content="noindex,nofollow"> while this
 * view is active so the page is never indexed.
 */

import { apiService } from "../services/api";
import type { PublicLeague, Round, Match, SetScore } from "../types/index";
import { computePlayerStats, renderStandingRow, EMPTY_STANDINGS_ROW } from "./leagueDetail/standings";
import { teamDisplayName } from "../utils/matchTeams";

export class PublicLeagueView {
  constructor(private container: HTMLElement, private token: string) {}

  async render(): Promise<void> {
    this.container.innerHTML = this.renderShell(`
      <div class="flex items-center justify-center gap-3 py-24 text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
        <span class="w-5 h-5 rounded-full border-2 border-primary/25 border-t-primary daylight:border-day-ink/15 daylight:border-t-day-ink animate-spin" aria-hidden="true"></span> Loading league…
      </div>
    `);

    try {
      const data = await apiService.getPublicLeague(this.token);
      this.container.innerHTML = this.renderShell(this.renderBody(data));
    } catch (error) {
      const message = error instanceof Error && error.message === "League not found"
        ? "This league link is invalid or has been removed."
        : "We couldn't load this league. Please try again later.";
      this.container.innerHTML = this.renderShell(`
        <div class="text-center py-24">
          <span class="material-symbols-outlined text-6xl text-slate-300 dark:text-slate-700 daylight:text-day-ink-muted mb-4 block">link_off</span>
          <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-2">League unavailable</h2>
          <p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${message}</p>
        </div>
      `);
    }
  }

  /** Branded, login-free page chrome wrapping arbitrary inner content. */
  private renderShell(inner: string): string {
    return `
      <div class="min-h-screen bg-background-light dark:bg-background-dark daylight:bg-day-base">
        <header class="border-b border-slate-200 dark:border-slate-800 daylight:border-day-ink/10">
          <div class="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <img src="/brand/icon-reverse.svg" alt="PadelPulse" class="size-9 hidden dark:block" />
              <img src="/brand/icon-color.svg" alt="PadelPulse" class="size-9 hidden daylight:block" />
              <span class="text-2xl text-slate-900 dark:text-white daylight:text-day-ink"><span class="font-medium">PADEL</span><span class="font-extrabold">PULSE</span></span>
            </div>
            <span class="text-xs font-bold uppercase tracking-wider text-slate-400 daylight:text-day-ink-muted flex items-center gap-1">
              <span class="material-symbols-outlined text-sm">visibility</span> Read-only
            </span>
          </div>
        </header>
        <main class="max-w-5xl mx-auto px-4 py-8">${inner}</main>
      </div>
    `;
  }

  private renderBody(data: PublicLeague): string {
    const { league, standings, rounds } = data;
    return `
      <div class="mb-8">
        <h1 class="text-3xl lg:text-4xl font-black text-slate-900 dark:text-white daylight:text-day-ink mb-2">${league.name}</h1>
        ${league.description ? `<p class="text-slate-600 dark:text-slate-400 daylight:text-day-ink-soft">${league.description}</p>` : ""}
      </div>

      <section class="mb-12">
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-4">Standings</h2>
        ${this.renderStandingsTable(standings, rounds)}
      </section>

      <section>
        <h2 class="text-2xl font-bold text-slate-900 dark:text-white daylight:text-day-ink mb-4">Fixtures &amp; Results</h2>
        ${this.renderRounds(rounds)}
      </section>
    `;
  }

  private renderStandingsTable(standings: PublicLeague["standings"], rounds: Round[]): string {
    const playerStats = computePlayerStats(rounds);
    const rows = standings.length
      ? standings.map((s, i) => `<tr class="border-t border-slate-100 dark:border-slate-800 daylight:border-day-ink/10">${renderStandingRow(s, i, playerStats)}</tr>`).join("")
      : EMPTY_STANDINGS_ROW;

    return `
      <div class="card overflow-x-auto p-0">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted">
              <th class="px-6 py-3">#</th>
              <th class="px-6 py-3">Player</th>
              <th class="px-6 py-3 text-center">P</th>
              <th class="px-6 py-3 text-center">W</th>
              <th class="px-6 py-3 text-center">Win%</th>
              <th class="px-6 py-3 text-center">Sets</th>
              <th class="px-6 py-3 text-center">Games</th>
              <th class="px-6 py-3 text-center">Diff</th>
              <th class="px-6 py-3 text-right">Points</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
    `;
  }

  private renderRounds(rounds: Round[]): string {
    if (!rounds.length) {
      return `<div class="card text-center text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted py-8">No rounds have been scheduled yet.</div>`;
    }
    return rounds.map((round) => `
      <div class="mb-6">
        <h3 class="text-sm font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 daylight:text-day-ink-muted mb-3">Round ${round.roundNumber}</h3>
        <div class="space-y-2">
          ${(round.matches || []).map((m) => this.renderMatchRow(m)).join("")}
        </div>
      </div>
    `).join("");
  }

  private renderMatchRow(match: Match): string {
    const isBye = match.matchType === "bye";
    const teamA = teamDisplayName(match, 0);
    const teamB = teamDisplayName(match, 1);
    const reported = match.status === "reported";

    if (isBye) {
      return `
        <div class="card p-3 flex items-center justify-between">
          <span class="font-semibold text-slate-800 dark:text-white daylight:text-day-ink text-sm">${teamA || teamB}</span>
          <span class="badge badge-primary">Bye</span>
        </div>
      `;
    }

    const sets: SetScore[] = (() => {
      try {
        return typeof match.sets === "string" ? JSON.parse(match.sets) : (match.sets || []);
      } catch {
        return [];
      }
    })();
    const score = reported && sets.length
      ? sets.map((s) => `${s.pair1score}-${s.pair2score}`).join("  ")
      : "";

    return `
      <div class="card p-3 flex items-center gap-3">
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-slate-800 dark:text-white daylight:text-day-ink text-sm truncate">${teamA}</div>
          <div class="font-semibold text-slate-800 dark:text-white daylight:text-day-ink text-sm truncate">${teamB}</div>
        </div>
        ${score
          ? `<div class="font-mono font-bold text-slate-900 dark:text-white daylight:text-day-ink whitespace-nowrap">${score}</div>`
          : `<span class="badge bg-slate-100 dark:bg-slate-800 daylight:bg-day-sunken text-slate-500 daylight:text-day-ink-muted">Pending</span>`}
      </div>
    `;
  }
}
